import { Select, Spin, Space, Input, Tooltip, type SelectProps } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';

import {
  NamedSelectWithExtrasWrapper,
  StyledSelectOption,
  TypeSelectWrapper,
  TypeValue,
} from './styled';
import { EAttributeType, TAttributeType } from '@src/emp-3.0/types';

type Option = {
  value: string;
  label: string;
  global?: boolean;
};

const EditableSelect = ({
  freeEdit,
  isGlobalAttribute,
  onChangeType,
  globalTagsOptions,
  eventTagsOptions,
  ...selectProps
}: SelectProps & {
  globalTagsOptions: Option[];
  eventTagsOptions: Option[];
  onChangeType: (type: TAttributeType) => void;
  isGlobalAttribute?: boolean;
  freeEdit: boolean;
}) => {
  const options = useMemo(() => {
    return [...globalTagsOptions, ...eventTagsOptions].map((option) => ({
      value: option.value,
      label: (
        <StyledSelectOption>
          <span>{option.label}</span>
          <span style={{ color: '#949494', fontSize: 12, marginRight: 8 }}>
            {option.global ? 'Global' : ''}
          </span>
        </StyledSelectOption>
      ),
      text: option.label,
    }));
  }, [globalTagsOptions, eventTagsOptions]);

  const select = (
    <Select
      placeholder="Type or select..."
      options={options}
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
      {...selectProps}
      style={{ minWidth: 100, ...selectProps.style }}
      optionLabelProp="text"
      suffixIcon={null}
      dropdownStyle={{
        minWidth: '275px',
        borderRadius: '4px 4px 0 0',
        padding: 0,
        ...selectProps.dropdownStyle,
      }}
      dropdownRender={(menu) => <Spin spinning={!!selectProps?.loading}>{menu}</Spin>}
    />
  );

  return (
    <NamedSelectWithExtrasWrapper>
      {selectProps?.value ? (
        <Space.Compact block>
          {select}
          {isGlobalAttribute === undefined || freeEdit ? (
            <TypeSelectWrapper>
              <Tooltip
                title={
                  isGlobalAttribute === undefined
                    ? undefined
                    : isGlobalAttribute
                    ? 'Tag key shared by all events'
                    : 'Tag key specific to certain events only'
                }
              >
                <Select
                  loading={selectProps.loading}
                  style={{ width: 135 }}
                  placeholder="Select type"
                  disabled={selectProps.disabled}
                  value={
                    isGlobalAttribute === undefined
                      ? undefined
                      : isGlobalAttribute
                      ? EAttributeType.Global
                      : EAttributeType.Event
                  }
                  onChange={(value) => onChangeType(value as TAttributeType)}
                  options={[
                    { value: EAttributeType.Global, label: 'Global' },
                    { value: EAttributeType.Event, label: 'Event Specific' },
                  ]}
                />
              </Tooltip>
            </TypeSelectWrapper>
          ) : selectProps.loading ? (
            <TypeValue>
              <LoadingOutlined />
            </TypeValue>
          ) : (
            <Tooltip
              title={
                isGlobalAttribute
                  ? 'Tag key shared by all events'
                  : 'Tag key specific to certain events only'
              }
            >
              <TypeValue
                style={
                  selectProps.disabled
                    ? { color: 'rgba(0, 0, 0, 0.25)', backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    : { color: 'rgba(0, 0, 0, 0.88)' }
                }
              >
                {isGlobalAttribute ? 'Global' : 'Event Specific'}
              </TypeValue>
            </Tooltip>
          )}
        </Space.Compact>
      ) : (
        select
      )}
    </NamedSelectWithExtrasWrapper>
  );
};

export default EditableSelect;
